<script setup>
import { ref, computed } from 'vue'
import Button from './reusable/Button.vue'
import FormInput from './reusable/FormInput.vue'
import FormTextarea from './reusable/FormTextarea.vue'
import FeatherIcon from './FeatherIcon.vue'

const props = defineProps({
  showModal: { type: Function, required: true },
  modal: { type: Boolean, required: true },
  categories: { type: Array, required: true }
})

// identify this form (useful in your API email subject)
const formId = 'project-modal'

const form = ref({
  name: '',
  email: '',
  project: '',
  details: '',
  _honeypot: '',
  formId
})

const sending = ref(false)
const success = ref(false)
const error = ref('')

const canSubmit = computed(() =>
    form.value.name.trim() &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.email) &&
    form.value.project &&
    form.value.details.trim() &&
    !sending.value
)

async function onSubmit () {
  error.value = ''
  success.value = false
  sending.value = true
  try {
    const r = await fetch('/api/contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: form.value.name,
        email: form.value.email,
        subject: `Project type: ${form.value.project}`,
        message: form.value.details,
        formId: formId,
        _honeypot: form.value._honeypot
      })
    })
    const data = await r.json().catch(() => ({}))
    if (!r.ok || !data.ok) throw new Error(data?.error || 'Failed to send')
    success.value = true
    form.value = { name: '', email: '', project: '', details: '', _honeypot: '', formId }
  } catch (e) {
    error.value = e?.message || 'Failed to send'
  } finally {
    sending.value = false
  }
}
</script>


<template>
  <transition name="fade">
    <!-- Make the OVERLAY itself scrollable on small screens -->
    <div v-show="props.modal" class="font-general-regular fixed inset-0 z-30 overflow-y-auto">
      <!-- Backdrop -->
      <div
          v-show="props.modal"
          @click="props.showModal()"
          class="bg-black/50 fixed inset-0 w-full h-full z-20"
      ></div>

      <!-- Content wrapper: top spacing on small; centered on md+ -->
      <main class="relative z-30 flex w-full min-h-full items-start md:items-center justify-center p-4 md:p-6">
        <transition name="fade-up-down">
          <!-- The MODAL is also scrollable (caps at viewport height) -->
          <div
              v-show="props.modal"
              class="w-full max-w-md md:max-w-xl bg-secondary-light dark:bg-primary-dark shadow-lg rounded-lg overflow-y-auto"
              style="max-height: 100dvh;"
          >
            <!-- Header -->
            <div class="flex justify-between gap-10 p-5 border-b border-ternary-light dark:border-ternary-dark sticky top-0 bg-secondary-light dark:bg-primary-dark">
              <h5 class="text-primary-dark dark:text-primary-light text-xl">
                What project are you looking for?
              </h5>
              <button
                  class="px-2 text-primary-dark dark:text-primary-light"
                  @click="props.showModal()"
                  title="Close"
                  type="button"
              >
                <FeatherIcon name="x" iconClass="w-4 h-4" />
              </button>
            </div>

            <!-- Body (scrolls as part of the modal) -->
            <div class="p-0 w-full">
              <form class="flex flex-col w-full text-left" @submit.prevent="onSubmit" novalidate>
                <div class="p-5 space-y-4">
                  <!-- Honeypot -->
                  <input
                      v-model="form._honeypot"
                      type="text"
                      tabindex="-1"
                      autocomplete="off"
                      class="hidden"
                      aria-hidden="true"
                  />

                  <FormInput
                      label="Full Name"
                      inputIdentifier="name"
                      v-model="form.name"
                      required
                  />
                  <FormInput
                      label="Email"
                      inputIdentifier="email"
                      inputType="email"
                      v-model="form.email"
                      required
                  />

                  <div>
                    <label
                        class="block mb-2 text-lg text-primary-dark dark:text-primary-light"
                        for="project"
                    >Project Type</label>
                    <select
                        id="project"
                        name="project"
                        v-model="form.project"
                        required
                        aria-label="Project Category"
                        class="w-full px-5 py-3 border border-gray-200 dark:border-secondary-dark rounded-md text-md bg-secondary-light dark:bg-ternary-dark text-primary-dark dark:text-ternary-light"
                    >
                      <option value="" disabled>Select a project type…</option>
                      <option
                          v-for="category in props.categories"
                          :key="category.id ?? category.value"
                          :value="category.value"
                      >
                        {{ category.name }}
                      </option>
                    </select>
                  </div>

                  <FormTextarea
                      label="Details"
                      textareaIdentifier="details"
                      v-model="form.details"
                      required
                  />
                </div>

                <!-- Footer -->
                <div class="px-5 py-4 border-t border-ternary-light dark:border-ternary-dark bg-secondary-light dark:bg-primary-dark">
                  <div class="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3 w-full">
                    <div class="h-5" aria-live="polite" role="status">
                      <p v-if="success" class="text-green-600 text-sm leading-none">Thanks! Your request was sent.</p>
                      <p v-else-if="error" class="text-red-600 text-sm leading-none">Error: {{ error }}</p>
                    </div>
                    <Button
                        :title="sending ? 'Sending…' : 'Send Request'"
                        class="px-4 sm:px-6 py-2 sm:py-2.5 text-white bg-indigo-500 hover:bg-indigo-600 rounded-md focus:ring-1 focus:ring-indigo-900 duration-500 disabled:opacity-60"
                        type="submit"
                        aria-label="Submit Request"
                        :disabled="!canSubmit"
                    />
                  </div>
                </div>
              </form>
            </div>
          </div>
        </transition>
      </main>
    </div>
  </transition>
</template>


<style scoped>
.modal-body { max-height: 570px; }
.bg-gray-800-opacity { background-color: #2d374850; }
@media screen and (max-width: 768px) { .modal-body { max-height: 400px; } }
.fade-up-down-enter-active, .fade-up-down-leave-active { transition: all 0.3s ease; }
.fade-up-down-enter, .fade-up-down-leave-to { transform: translateY(10%); opacity: 0; }
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s; }
.fade-enter, .fade-leave-to { opacity: 0; }
</style>
